import { fitText, textWidth } from '../ComboChart/labels';
import { ValueScale, valueScale } from '../ComboChart/scale';
import { ComboBarStyle } from '../ComboChart/types';

export interface TwoSidedBarsSide {
  key: string;
  name: string;
  color: string;
  /** One per category; undefined draws no bar. Negatives draw as zero. */
  values: (number | undefined)[];
  labels?: (string | undefined)[];
}

export interface TwoSidedLayoutInput {
  width: number;
  categories: string[];
  left: TwoSidedBarsSide;
  right?: TwoSidedBarsSide;
  dataLabels: boolean;
  bar: ComboBarStyle;
  format: (value: number) => string;
  /** A value short, when full labels would squeeze the bars. */
  compact?: (value: number) => string;
  fontSize: number;
}

export interface TwoSidedLayout {
  /** The category names as drawn (cut to fit), down the middle. */
  names: string[];
  /** Where each side's bars start, either side of the names. */
  leftAxis: number;
  rightAxis: number;
  /** The longest a bar may grow. */
  half: number;
  scale: ValueScale;
  /** Each side's data labels as drawn: full, or short to save room. */
  leftLabels: (string | undefined)[];
  rightLabels: (string | undefined)[];
  /** Height of one category's row. */
  band: number;
  top: number;
  height: number;
}

export const kTwoSidedBarThickness = 14;

/** Full labels may take this share of the width; past it they go short. */
const kLabelShare = 0.16;

/** The fewest pixels a half keeps for its bars. */
const kMinHalf = 24;

function widest(labels: (string | undefined)[], f: number): number {
  return Math.max(0, ...labels.map(text => (text ? textWidth(text, f) : 0)));
}

function labelsOf(
  side: TwoSidedBarsSide | undefined,
  compact: ((value: number) => string) | undefined,
): (string | undefined)[] {
  if (!side) return [];
  if (!compact) return side.labels ?? [];
  return side.values.map(value =>
    value === undefined || !Number.isFinite(value) ? undefined : compact(value),
  );
}

/** The height a chart of these rows takes. */
export function twoSidedBarsHeight(
  categories: number,
  bar: ComboBarStyle,
  fontSize: number,
): number {
  const band = kTwoSidedBarThickness / (1 - bar.gap);
  return Math.ceil(fontSize + 10 + band * categories + fontSize + 10);
}

/**
 * Where a pyramid's parts go: the names in a middle column, each half's
 * bars on one scale, room past the ends for the labels and the outer tick.
 * Labels go short when in full they would take too much of the width.
 */
export function twoSidedLayout(input: TwoSidedLayoutInput): TwoSidedLayout {
  const { width, categories, left, right, fontSize: f } = input;
  const band = kTwoSidedBarThickness / (1 - input.bar.gap);
  const names = categories.map(name => fitText(name, width * 0.26, f));
  const mid = Math.max(0, ...names.map(name => textWidth(name, f))) + 16;
  const full = [labelsOf(left, undefined), labelsOf(right, undefined)];
  const short =
    input.dataLabels &&
    input.compact &&
    Math.max(widest(full[0], f), widest(full[1], f)) > width * kLabelShare;
  const [leftLabels, rightLabels] = short
    ? [labelsOf(left, input.compact), labelsOf(right, input.compact)]
    : full;
  const room = input.dataLabels
    ? Math.max(widest(leftLabels, f), widest(rightLabels, f)) + 6
    : 0;
  const positive = [left, right]
    .flatMap(side => side?.values ?? [])
    .filter(
      (value): value is number => value !== undefined && Number.isFinite(value),
    )
    .map(value => Math.max(value, 0));
  // The outer tick is centred on the half's end: leave it half its width.
  const outer = valueScale(positive, 1, { min: 0 }, 2).max;
  const tickRoom = textWidth(input.format(outer), f) / 2 + 2;
  const half = Math.max(
    (width - mid - 2 * Math.max(room, tickRoom) - 8) / 2,
    kMinHalf,
  );
  return {
    names,
    leftAxis: width / 2 - mid / 2,
    rightAxis: width / 2 + mid / 2,
    half,
    scale: valueScale(positive, half, { min: 0 }, 2),
    leftLabels,
    rightLabels,
    band,
    top: f + 10,
    height: twoSidedBarsHeight(categories.length, input.bar, f),
  };
}
